'use client'

// import { USAMap } from '@/lib/usa-map.js'
import { usePathname } from 'next/navigation'
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { SlidersHorizontalIcon, XIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import { Button } from '@/components/ui/button'
import { useLegislativeContext } from '@/providers/LegislativeProvider'
import { useLenisContext } from '@/providers/Lenis'
import { cn } from '@/utilities/ui'

import { BillFeaturedStacks } from './components/BillFeaturedStacks'
import { BillPaginatedList } from './components/BillPaginatedList'
import { LegislativeTrackerSidebar } from './components/LegislativeTrackerSidebar'

// Loaded here (not just inside the lazy USAMap chunk) so the Suspense fallback
// below can render the same usa-map-spinner while the chunk downloads.
import '@/components/legislative-tracker/USAMap/USAMap.css'

import type { USAMapRef } from '@/components/legislative-tracker/USAMap/types'

// lazy (not next/dynamic) so the forwarded mapRef keeps working; splits the
// three.js map chunk out of the tracker's initial bundle.
const USAMap = lazy(() =>
  import('@/components/legislative-tracker/USAMap').then((m) => ({ default: m.USAMap })),
)

type LegislativeTrackerProps = {
  /** Scope for filtering bills: 'us' | 'all' | state code (e.g., 'ca'). Undefined = homepage view */
  scope?: string
}

export const LegislativeTracker = ({ scope }: LegislativeTrackerProps) => {
  const mapRef = useRef<USAMapRef | null>(null)
  const {
    selectedLegislativeTab,
    setSelectedLegislativeTab,
    selectedState,
    setSelectedState,
    filterOpen,
    setFilterOpen,
  } = useLegislativeContext()
  const { isMobile } = useLenisContext()
  const pathname = usePathname()
  const [previousPathname, setPreviousPathname] = useState(pathname)
  const isPageChange = previousPathname !== pathname

  const isScoped = Boolean(scope)

  // Sync scope prop with provider state
  useEffect(() => {
    if (!scope) return

    if (scope === 'us') {
      setSelectedLegislativeTab('us')
      setSelectedState(undefined)
    } else if (scope === 'all') {
      setSelectedLegislativeTab('all-bills')
      setSelectedState(undefined)
    } else {
      // State code (e.g., 'ca', 'ma')
      setSelectedLegislativeTab('state')
      setSelectedState(scope.toLowerCase())
    }
  }, [scope, setSelectedLegislativeTab, setSelectedState])

  // Sync sidebar tab → map (when user selects tab in sidebar, update map view)
  useEffect(() => {
    if (isScoped) return
    const map = mapRef.current
    if (!map) return

    if (selectedLegislativeTab === 'all-bills') {
      map.deselect()
    } else if (selectedLegislativeTab === 'us') {
      map.selectFederal()
    } else if (selectedLegislativeTab === 'state' && selectedState) {
      map.selectState(selectedState)
    }
  }, [isScoped, selectedLegislativeTab, selectedState])

  // Track pathname changes for animation. isPageChange must be true for exactly
  // one committed render (the navigation frame) and then reset. Resetting during
  // render would re-render before commit, so the fade config would never be
  // committed; the reset therefore has to happen post-commit in an effect.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see comment above
    setPreviousPathname(pathname)
  }, [pathname])

  // Sidebar animation config
  const sidebarMotion = useMemo(() => {
    if (isPageChange) {
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      }
    }

    if (isScoped && isMobile) {
      return {
        initial: filterOpen ? false : { opacity: 0, x: '100%' },
        animate: filterOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: '100%' },
        exit: filterOpen ? { opacity: 0, x: '100%' } : { opacity: 1, x: 0 },
      }
    }

    return {
      initial: false,
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0 },
    }
  }, [filterOpen, isMobile, isPageChange, isScoped])

  const handleFilterToggle = () => setFilterOpen(!filterOpen)

  return (
    <div
      className={cn(
        'relative mt-10 grid-cols-1 gap-4 lg:grid lg:h-full lg:flex-1 lg:grow-0 lg:grid-cols-[minmax(0,30%)_minmax(0,70%)] lg:grid-rows-[minmax(0,780px)]',
        isScoped
          ? 'gap-5 lg:grid-cols-[minmax(324px,340px)_minmax(0,1fr)] lg:grid-rows-[auto_40px] xl:gap-8'
          : 'mt-5 grid lg:mt-10',
      )}
    >
      {/* Mobile filter toggle button */}
      <Button
        variant="icon"
        className={cn(
          'fixed right-4 bottom-4 z-50 size-11 cursor-pointer border border-accent-2 bg-accent shadow-md shadow-primary/10',
          isScoped ? 'lg:hidden' : 'hidden',
        )}
        aria-label="Toggle filter menu"
        onClick={handleFilterToggle}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={filterOpen ? 'close' : 'open'}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.1, ease: 'easeOut' }}
          >
            {filterOpen ? (
              <XIcon className="size-4.5" />
            ) : (
              <SlidersHorizontalIcon className="size-4.5" />
            )}
          </motion.div>
        </AnimatePresence>
      </Button>

      {/* Sidebar: filters + bill card grid (on homepage) */}
      <motion.div
        initial={sidebarMotion.initial}
        animate={sidebarMotion.animate}
        exit={sidebarMotion.exit}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className={cn(
          'flex flex-col items-start',
          isScoped
            ? 'gap-4 self-start overflow-y-auto rounded-xl border border-border bg-background p-4 max-lg:fixed max-lg:inset-x-4 max-lg:top-21 max-lg:z-20 max-lg:max-h-[calc(100vh-84px-4rem)] max-lg:w-[calc(100%-32px)] max-lg:rounded-xl max-lg:border max-lg:border-border max-lg:bg-background max-lg:p-4 max-lg:shadow-md max-lg:shadow-primary/10 md:px-3'
            : 'max-lg:order-2',
          isScoped && isMobile && !filterOpen && 'pointer-events-none',
        )}
        data-lenis-prevent={isScoped && isMobile ? 'true' : undefined}
      >
        <LegislativeTrackerSidebar isScoped={isScoped} />
        {!isScoped && (
          <div
            className="scrollbar-hidden flex w-full flex-col flex-wrap gap-4 [scrollbar-gutter:stable] md:flex-row lg:max-h-[calc(100vh-200px)] lg:flex-1 lg:content-start lg:overflow-y-auto lg:px-1 [&>.bill-card]:mt-4"
            data-lenis-prevent
          >
            <BillFeaturedStacks />
            <div className="mx-auto -mt-2 w-fit pb-11 text-center">
              <Button
                asChild
                variant="link"
                className="text-sm text-muted-foreground transition-colors"
              >
                Missing a bill?{' '}
                <a
                  href="mailto:legislation@mahaaction.org"
                  className="font-medium text-primary hover:text-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Email us
                </a>
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Filter blur */}
      <motion.div
        className="fixed top-0 left-0 z-19 size-full bg-black/5 backdrop-blur-xs lg:hidden"
        initial={false}
        animate={
          filterOpen ? { opacity: 1, visibility: 'visible' } : { opacity: 0, visibility: 'hidden' }
        }
        transition={{ duration: 0.2, delay: filterOpen ? 0 : 0.1, ease: 'easeOut' }}
        onClick={() => setFilterOpen(false)}
      />

      {/* Main content area */}
      {isScoped ? (
        <div className="flex min-w-0 flex-col gap-4">
          <BillPaginatedList />
        </div>
      ) : (
        <div className="relative mx-auto w-full max-lg:aspect-959/593 max-lg:max-w-115 lg:h-full">
          <Suspense
            fallback={
              <div className="usa-map-loading">
                <div className="usa-map-spinner" />
              </div>
            }
          >
            <USAMap
              ref={mapRef}
              mapDataUrl="/usa-map-3d.json"
              showStateLabels={true}
              randomColorOnClick={false}
              showUI={false}
              className="max-lg:pointer-events-none max-lg:order-1 lg:-top-32"
              onStateClick={(id) => {
                setSelectedLegislativeTab('state')
                setSelectedState(id)
              }}
              onDeselect={() => {
                setSelectedLegislativeTab('all-bills')
                setSelectedState(undefined)
              }}
            />
          </Suspense>
        </div>
      )}
    </div>
  )
}
